<template>
  <BaseLocationInput
    :model-value="modelValue"
    :label="label"
    :help-text="helpText"
    :hide-label="hideLabel"
    autocomplete-type="(regions)"
    @place-selected="handlePlaceSelected"
  >
    <div v-if="selectedLocations.length > 0" class="mt-2 flex flex-wrap gap-2">
      <Badge
        v-for="(location, index) in selectedLocations"
        :key="typeof location === 'string' ? location : [location.city, location.state].filter(Boolean).join(', ')"
        variant="blue"
        class="cursor-pointer hover:bg-blue-200 mr-2"
        @click="removeLocation(index)"
      >
        {{ typeof location === 'string' ? location : [location.city, location.state].filter(Boolean).join(', ') }}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="ml-1"
          @click.stop="removeLocation(index)"
        >
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </Badge>
    </div>
  </BaseLocationInput>
</template>

<script setup>
import { ref, watch } from 'vue';
import BaseLocationInput from './BaseLocationInput.vue';
import { Badge } from '@/components/ui/badge';

const emit = defineEmits(['update:modelValue']);

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  label: {
    type: String,
    default: 'Location',
  },
  helpText: {
    type: String,
    required: false,
  },
  hideLabel: {
    type: Boolean,
    default: false,
  },
});

const selectedLocations = ref([]);

if (Array.isArray(props.modelValue)) {
  selectedLocations.value = deduplicateLocations([...props.modelValue]);
}

function deduplicateLocations(locations) {
  if (!Array.isArray(locations)) return [];
  const seen = new Set();
  return locations.filter(loc => {
    const key = `${loc.city || ''},${loc.state || ''}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function isSameCity(a, b) {
  return a.city === b.city && a.state === b.state;
}

function isNewLocation(location) {
  return !selectedLocations.value.some(init => isSameCity(init, location));
}

function handlePlaceSelected(item) {
  if (window.google && window.google.maps && window.google.maps.places) {
    const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
    placesService.getDetails(
      {
        placeId: item.place_id,
        fields: ['geometry'],
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place && place.geometry) {
          const locationData = {
            city: item.terms[0].value,
            state: item.terms[1].value,
            country: item.terms[2]?.value || 'USA',
            latitude: place.geometry.location.lat(),
            longitude: place.geometry.location.lng(),
          };
          if (isNewLocation(locationData)) {
            selectedLocations.value = deduplicateLocations([...selectedLocations.value, locationData]);
            emit('update:modelValue', selectedLocations.value);
          }
        } else {
          const locationData = {
            city: item.terms[0].value,
            state: item.terms[1].value,
            country: item.terms[2]?.value || 'USA',
            latitude: 0,
            longitude: 0,
          };
          if (isNewLocation(locationData)) {
            selectedLocations.value = deduplicateLocations([...selectedLocations.value, locationData]);
            emit('update:modelValue', selectedLocations.value);
          }
        }
      }
    );
  } else {
    const locationData = {
      city: item.terms[0].value,
      state: item.terms[1].value,
      country: item.terms[2]?.value || 'USA',
      latitude: 0,
      longitude: 0,
    };
    if (isNewLocation(locationData)) {
      selectedLocations.value = deduplicateLocations([...selectedLocations.value, locationData]);
      emit('update:modelValue', selectedLocations.value);
    }
  }
}

function removeLocation(index) {
  selectedLocations.value = selectedLocations.value.filter((_, i) => i !== index);
  emit('update:modelValue', selectedLocations.value);
}

watch(
  () => props.modelValue,
  newValue => {
    if (Array.isArray(newValue)) {
      const newLocations = deduplicateLocations([...newValue]);
      if (JSON.stringify(newLocations) !== JSON.stringify(selectedLocations.value)) {
        selectedLocations.value = newLocations;
      }
    } else {
      selectedLocations.value = [];
    }
  },
  { deep: true, immediate: true }
);
</script>

<style scoped>
.clickable-item {
  cursor: pointer;
  padding: 8px;
  transition: background-color 0.3s;
  display: flex;
  align-items: center;
}

.clickable-item:hover {
  background-color: #f0f0f0;
}

.no-results-item {
  padding: 12px;
  text-align: center;
  color: #666;
  font-style: italic;
}

.suggestions-dropdown {
  position: fixed;
  z-index: 99999;
  max-height: 300px;
  overflow-y: auto;
  background-color: white;
  border-radius: 4px;
  box-shadow:
    0 4px 6px rgba(0, 0, 0, 0.1),
    0 1px 3px rgba(0, 0, 0, 0.08);
  list-style: none;
  padding: 0;
  /* Remove left/right positioning, will be set dynamically */
}

@media (max-width: 640px) {
  .suggestions-dropdown {
    position: fixed;
    width: 90%;
    max-height: 40vh;
    left: 5%;
    top: 30%;
    z-index: 99999;
  }
}

.location-icon {
  margin-right: 8px;
  min-width: 16px;
  color: #666;
}

.relative {
  position: relative;
  z-index: 10;
}

.relative:first-child {
  position: relative;
  z-index: 100;
}

.google-maps-input-container {
  position: relative;
  z-index: 100;
}
</style>
